// src/stores/codebookStore.ts
// Zustand store for BookMate state — survives tab switches (component unmount/remount)

import { create } from 'zustand';
import { CodebookGetResponse } from '../types/chat';
import { PathLaunchContext } from '../types/paths';

// pathContext is set when the tutorial was launched from a Learning Path deep
// link (/bookmate?prompt=...&path=...&node=...). The viewer uses it to replace
// the "What's Next?" follow-ups with a "Back to your path" card so path users
// aren't pulled off their route into Bookmate's own sequence.
export type { PathLaunchContext };

interface CodebookState {
  // Generation state
  isGenerating: boolean;
  statusMessage: string;
  error: string | null;
  currentCodebook: CodebookGetResponse | null;
  showViewer: boolean;
  activeCodebookId: string | null;
  elapsedSeconds: number;
  input: string;
  pathContext: PathLaunchContext | null;

  // Actions
  setIsGenerating: (val: boolean) => void;
  setStatusMessage: (msg: string) => void;
  setError: (err: string | null) => void;
  setCurrentCodebook: (cb: CodebookGetResponse | null) => void;
  setShowViewer: (val: boolean) => void;
  setActiveCodebookId: (id: string | null) => void;
  setElapsedSeconds: (val: number | ((prev: number) => number)) => void;
  setInput: (val: string) => void;
  setPathContext: (ctx: PathLaunchContext | null) => void;

  // Reset
  resetGeneration: () => void;
}

export const useCodebookStore = create<CodebookState>((set) => ({
  isGenerating: false,
  statusMessage: '',
  error: null,
  currentCodebook: null,
  showViewer: false,
  activeCodebookId: null,
  elapsedSeconds: 0,
  input: '',
  pathContext: null,

  setIsGenerating: (val) => set({ isGenerating: val }),
  setStatusMessage: (msg) => set({ statusMessage: msg }),
  setError: (err) => set({ error: err }),
  setCurrentCodebook: (cb) => set({ currentCodebook: cb }),
  setShowViewer: (val) => set({ showViewer: val }),
  setActiveCodebookId: (id) => set({ activeCodebookId: id }),
  setElapsedSeconds: (val) => set((state) => ({
    elapsedSeconds: typeof val === 'function' ? val(state.elapsedSeconds) : val,
  })),
  setInput: (val) => set({ input: val }),
  setPathContext: (ctx) => set({ pathContext: ctx }),

  resetGeneration: () => set({
    isGenerating: false,
    statusMessage: '',
    error: null,
    currentCodebook: null,
    showViewer: false,
    activeCodebookId: null,
    elapsedSeconds: 0,
    pathContext: null,
  }),
}));
